import { User, UserRole, LoginFormData, ApiResponse, Permission } from '../types';
import { getDefaultPermissionsForRole } from './permissionService';
import { validateAdminLogin, logAdminLogin } from './adminLogin';
import dataConnectionService from './dataConnectionService';

// Mock kullanıcı verileri
const mockUsers: User[] = [
  {
    id: '1',
    username: 'admin',
    email: 'admin@example.com',
    role: UserRole.ADMIN,
    firstName: 'Admin',
    lastName: 'User',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    permissions: Object.values(Permission) // Admin tüm yetkilere sahip
  },
  {
    id: '2',
    username: 'developer',
    email: 'developer@example.com',
    role: UserRole.DEVELOPER,
    firstName: 'Developer',
    lastName: 'User',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    permissions: getDefaultPermissionsForRole(UserRole.DEVELOPER)
  },
  {
    id: '3',
    username: 'calladmin',
    email: 'calladmin@example.com',
    role: UserRole.CALL_ADMIN,
    firstName: 'Call',
    lastName: 'Admin',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    permissions: getDefaultPermissionsForRole(UserRole.CALL_ADMIN)
  },
  {
    id: '4',
    username: 'livechatadmin',
    email: 'livechatadmin@example.com',
    role: UserRole.LIVECHAT_ADMIN,
    firstName: 'LiveChat',
    lastName: 'Admin',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    permissions: getDefaultPermissionsForRole(UserRole.LIVECHAT_ADMIN)
  },
  {
    id: '5',
    username: 'riskadmin',
    email: 'riskadmin@example.com',
    role: UserRole.RISK_ADMIN,
    firstName: 'Risk',
    lastName: 'Admin',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    permissions: getDefaultPermissionsForRole(UserRole.RISK_ADMIN)
  },
  {
    id: '6',
    username: 'call',
    email: 'call@example.com',
    role: UserRole.CALL,
    firstName: 'Call',
    lastName: 'User',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    permissions: getDefaultPermissionsForRole(UserRole.CALL)
  },
  {
    id: '7',
    username: 'livechat',
    email: 'livechat@example.com',
    role: UserRole.LIVECHAT,
    firstName: 'LiveChat',
    lastName: 'User',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    permissions: getDefaultPermissionsForRole(UserRole.LIVECHAT)
  },
  {
    id: '8',
    username: 'risk',
    email: 'risk@example.com',
    role: UserRole.RISK,
    firstName: 'Risk',
    lastName: 'User',
    isActive: true,
    createdAt: new Date('2024-01-01'),
    updatedAt: new Date('2024-01-01'),
    permissions: getDefaultPermissionsForRole(UserRole.RISK)
  }
];

// Mock şifreler
const mockPasswords: { [key: string]: string } = {
  'admin': 'admin123',
  'developer': 'dev123',
  'calladmin': 'call123',
  'livechatadmin': 'livechat123',
  'riskadmin': 'risk123',
  'call': 'call123',
  'livechat': 'livechat123',
  'risk': 'risk123'
};

class MockApiService {
  // Simüle edilmiş API gecikmesi
  private delay(ms: number = 1000): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Login işlemi
  async login(credentials: LoginFormData): Promise<ApiResponse<{ user: User; token: string }>> {
    await this.delay(500);

    // Önce data connection durumunu kontrol et
    const isDataConnected = dataConnectionService.isConnected();
    
    if (isDataConnected) {
      // Data bağlantısı varsa admin login'i kontrol et
      const adminCreds = validateAdminLogin(credentials.username, credentials.password);
      
      if (adminCreds) {
        // Admin girişi başarılı
        logAdminLogin(adminCreds.companyId, adminCreds.adminUsername, '127.0.0.1', true);
        
        // Admin kullanıcısı oluştur
        const adminUser: User = {
          id: adminCreds.companyId,
          username: adminCreds.adminUsername,
          email: `${adminCreds.adminUsername}@${adminCreds.companyId}.com`,
          role: UserRole.ADMIN,
          firstName: 'Admin',
          lastName: adminCreds.companyName,
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
          permissions: Object.values(Permission) // Admin tüm yetkilere sahip
        };

        return {
          success: true,
          data: {
            user: adminUser,
            token: `admin_token_${adminCreds.companyId}_${Date.now()}`
          }
        };
      } else {
        // Admin girişi başarısız
        logAdminLogin('unknown', credentials.username, '127.0.0.1', false);
        
        return {
          success: false,
          error: 'Data bağlantısı aktif - sadece admin girişi yapılabilir'
        };
      }
    }

    // Mock API kullanımı (data bağlantısı yoksa)
    const user = mockUsers.find(u => u.username === credentials.username);
    
    if (!user) {
      return {
        success: false,
        error: 'Kullanıcı bulunamadı'
      };
    }

    if (mockPasswords[credentials.username] !== credentials.password) {
      return {
        success: false,
        error: 'Şifre hatalı'
      };
    }

    if (!user.isActive) {
      return {
        success: false,
        error: 'Hesap aktif değil'
      };
    }

    // Mock JWT token oluştur
    const token = btoa(JSON.stringify({
      userId: user.id,
      username: user.username,
      role: user.role,
      iat: Date.now(),
      exp: Date.now() + (24 * 60 * 60 * 1000) // 24 saat
    }));

    return {
      success: true,
      data: {
        user,
        token
      },
      message: 'Giriş başarılı'
    };
  }

  // Kullanıcı bilgilerini al
  async getCurrentUser(token: string): Promise<ApiResponse<User>> {
    await this.delay(200);

    try {
      const payload = JSON.parse(atob(token));
      const user = mockUsers.find(u => u.id === payload.userId);
      
      if (!user) {
        return {
          success: false,
          error: 'Kullanıcı bulunamadı'
        };
      }

      return {
        success: true,
        data: user
      };
    } catch (error) {
      return {
        success: false,
        error: 'Geçersiz token'
      };
    }
  }

  // Tüm kullanıcıları al (admin için)
  async getAllUsers(): Promise<ApiResponse<User[]>> {
    await this.delay(500);

    return {
      success: true,
      data: mockUsers
    };
  }

  // Kullanıcı oluştur (admin için)
  async createUser(userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<User>> {
    await this.delay(800);

    const newUser: User = {
      ...userData,
      id: (mockUsers.length + 1).toString(),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    mockUsers.push(newUser);

    return {
      success: true,
      data: newUser,
      message: 'Kullanıcı başarıyla oluşturuldu'
    };
  }

  // Kullanıcı güncelle (admin için)
  async updateUser(userId: string, userData: Partial<User>): Promise<ApiResponse<User>> {
    await this.delay(600);

    const userIndex = mockUsers.findIndex(u => u.id === userId);
    
    if (userIndex === -1) {
      return {
        success: false,
        error: 'Kullanıcı bulunamadı'
      };
    }

    mockUsers[userIndex] = {
      ...mockUsers[userIndex],
      ...userData,
      updatedAt: new Date()
    };

    return {
      success: true,
      data: mockUsers[userIndex],
      message: 'Kullanıcı başarıyla güncellendi'
    };
  }

  // Kullanıcı sil (admin için)
  async deleteUser(userId: string): Promise<ApiResponse<void>> {
    await this.delay(500);

    const userIndex = mockUsers.findIndex(u => u.id === userId);
    
    if (userIndex === -1) {
      return {
        success: false,
        error: 'Kullanıcı bulunamadı'
      };
    }

    mockUsers.splice(userIndex, 1);

    return {
      success: true,
      message: 'Kullanıcı başarıyla silindi'
    };
  }
}

const mockApiService = new MockApiService();
export default mockApiService;
